'use client'

import { Fragment, useEffect, useRef, useState } from 'react'
import { LinkButton } from '@/components/ui/button'
import { HiveMark } from '@/components/brand/product-marks'
import { HIVE_FAQS } from './faqs'
import './hive-page.css'

/**
 * /hive — a direct port of `public/radar-branding/hive-redesign.html`.
 *
 * That file is the design contract: markup, copy, and section order match it
 * one-for-one. Styling lives in the sibling `hive-page.css`, scoped under
 * `.hive-page`.
 *
 * Every class carries an `hv-` prefix (mechanically: `hv-` + the source's own
 * name). On /vector, four unprefixed class names collided with globals.css or
 * a Tailwind utility and silently repainted the page; prefixing makes that
 * whole category impossible here instead of fixing collisions one at a time.
 *
 * Five deliberate breaks from the source:
 *   1. Its nav, skip link, footer, and scroll-progress bar are dropped — the
 *      site header, footer, and root-layout `ScrollProgress` already provide
 *      them.
 *   2. CTAs are the site's `LinkButton`, not the mock's `.btn` classes.
 *   3. The inline hive SVG is the canonical `HiveMark` component, which is the
 *      only sanctioned way to render product branding. The mock's own path
 *      data is already identical to it.
 *   4. The family row's "Radar" link points at `/platform`; the mock's
 *      `/radar` is not a route on this site and would 404.
 *   5. There is no lead form: `BookCallCTA` was removed on request, so the
 *      "Request pilot access" buttons go to /contact-us rather than the
 *      mock's `#book-call` anchor.
 */

const COMMERCIAL = [
  ['Build', '$65,000', 'One-time · 12 weeks'],
  ['Platform', '$3,000/mo', '12-month minimum'],
  ['Usage', '$2 / resolution', 'Successful event'],
  ['Ownership', 'Your application code', 'Source, data & deployment'],
] as const

/** The four context-bundle chips, in the order the legend lists them. */
const BUNDLE_KEYS = [
  ['var(--h-text)', 'Account'],
  ['var(--h-cyan)', 'Intent'],
  ['var(--h-yellow)', 'Urgency'],
  ['var(--h-pink)', 'History'],
] as const

const RELAY_ROUTES = [
  {
    id: 'lead',
    path: 'M84 212H306C390 212 408 82 518 82',
    begin: '.45s',
    keyPoints: '0;0;0.46;0.46;1;1',
    end: 'translate(518 82)',
    opacity: '0;0;1;1;0',
    opacityTimes: '0;.08;.14;.84;1',
  },
  {
    id: 'support',
    path: 'M84 212H306H518',
    begin: '2.7s',
    keyPoints: '0;0;0.51;0.51;1;1',
    end: 'translate(518 210)',
    opacity: '0;0;1;1;0',
    opacityTimes: '0;.08;.14;.84;1',
  },
  {
    id: 'operations',
    path: 'M84 212H306C390 212 408 338 518 338',
    begin: '4.95s',
    keyPoints: '0;0;0.46;0.46;1;1',
    end: 'translate(518 338)',
    opacity: '0;0;1;1',
    opacityTimes: '0;.08;.14;1',
  },
] as const

function ContextBundleSquares() {
  return (
    <>
      <rect className='hv-bundle-account' x='-9' y='-9' width='8' height='8' />
      <rect className='hv-bundle-intent' x='1' y='-9' width='8' height='8' />
      <rect className='hv-bundle-urgency' x='-9' y='1' width='8' height='8' />
      <rect className='hv-bundle-history' x='1' y='1' width='8' height='8' />
    </>
  )
}

const RESET_CARDS = [
  ['Sales assistant', '“Tell me your company, team size, and what you need.”'],
  ['Support bot', '“Can you repeat your account details and the issue?”'],
  ['Ops inbox', '“Please forward the original request and any screenshots.”'],
] as const

const THREAD_CONTEXT = [
  ['Account', 'Acme B2B'],
  ['Intent', 'Upgrade plan'],
  ['Urgency', 'Before renewal'],
  ['History', '2 prior contacts'],
] as const

const HANDOFF_STEPS = [
  ['01', 'Lead intelligence', 'understands the request'],
  ['02', 'Support', 'checks account history'],
  ['03', 'Coordinator', 'routes next action'],
] as const

const ARCH_LEFT = [
  ['hv-arch-sales', 'SA', 'Sales', 'Qualification, account context, routing.'],
  ['hv-arch-support', 'SP', 'Support', 'Issue history, answers, escalation.'],
] as const

const ARCH_RIGHT = [
  ['hv-arch-ops', 'OP', 'Operations', 'Workflow actions, tool coordination.'],
  ['hv-arch-success', 'CS', 'Success', 'Health signals, follow-up, retention.'],
] as const

const BENEFITS = [
  ['Unified memory', 'Agents draw from one current customer state.'],
  ['Seamless handoffs', 'History and rationale travel with ownership.'],
  ['Cross-agent learning', 'Validated patterns can improve the wider system.'],
  ['Human escalation', 'Low-confidence cases arrive with context intact.'],
] as const

const USE_CASES = [
  {
    id: 'insurance',
    tab: 'Insurance',
    number: '01 / 04',
    title: 'Insurance agent team',
    copy: 'Coordinate visual assessment, pricing, policy matching, and a clear customer reply without dropping the original context.',
    capabilities: [
      'Image-based assessment',
      'PHP quote generation',
      'Installment calculation',
      'Policy identification',
    ],
    chain: [
      [
        '01',
        'Vision agent',
        'reads the uploaded item or document',
        'context added',
      ],
      ['02', 'Pricing agent', 'calculates quote and installments', 'handoff'],
      ['03', 'Policy agent', 'matches requirements and exclusions', 'checked'],
      [
        '04',
        'Coordinator',
        'responds or escalates with the full thread',
        'resolved / human',
      ],
    ],
    impactLabel: 'Expected impact · not a customer result',
    impact: [
      '24/7 availability',
      '<2 minute response target',
      'Multimodal intake',
    ],
  },
  {
    id: 'logistics',
    tab: 'Logistics',
    number: '02 / 04',
    title: 'Logistics agent team',
    copy: 'Keep shipment, carrier, insurance, and customer-service context connected across every delivery question.',
    capabilities: [
      'Shipment tracking',
      'Cargo insurance',
      'Delivery timing',
      'Multi-carrier context',
    ],
    chain: [
      [
        '01',
        'Intake agent',
        'identifies shipment and customer',
        'context loaded',
      ],
      [
        '02',
        'Tracking agent',
        'checks status across carrier systems',
        'tool call',
      ],
      ['03', 'Coverage agent', 'reviews cargo policy details', 'handoff'],
      [
        '04',
        'Coordinator',
        'answers or routes the exception',
        'resolved / human',
      ],
    ],
    impactLabel: 'Illustrative workflow',
    impact: [
      'One shipment thread',
      'Cross-carrier context',
      'Exception escalation',
    ],
  },
  {
    id: 'warehouse',
    tab: 'Warehouse',
    number: '03 / 04',
    title: 'Warehouse agent team',
    copy: 'Connect inventory questions to actual stock, locations, reorder logic, and the people who own exceptions.',
    capabilities: [
      'Inventory lookup',
      'Stock checks',
      'Reorder alerts',
      'WMS integration',
    ],
    chain: [
      [
        '01',
        'Inventory agent',
        'checks SKU, quantity, and location',
        'live context',
      ],
      ['02', 'Forecast agent', 'assesses reorder threshold', 'analysis'],
      ['03', 'WMS agent', 'prepares the approved action', 'controlled'],
      ['04', 'Coordinator', 'logs completion or escalates conflict', 'audited'],
    ],
    impactLabel: 'Illustrative workflow',
    impact: ['Shared SKU state', 'Controlled actions', 'Conflict detection'],
  },
  {
    id: 'hr',
    tab: 'HR',
    number: '04 / 04',
    title: 'HR agent team',
    copy: 'Carry employee context through policy questions, onboarding tasks, document routing, and sensitive escalations.',
    capabilities: [
      'Employee inquiries',
      'Policy and benefits',
      'Onboarding workflows',
      'Document routing',
    ],
    chain: [
      [
        '01',
        'People agent',
        'identifies question and permissions',
        'access checked',
      ],
      ['02', 'Policy agent', 'retrieves the relevant source', 'grounded'],
      ['03', 'Workflow agent', 'routes forms or onboarding work', 'handoff'],
      [
        '04',
        'Human partner',
        'receives sensitive cases with context',
        'escalated',
      ],
    ],
    impactLabel: 'Illustrative workflow',
    impact: [
      'Permission-aware answers',
      'Source visibility',
      'Human escalation',
    ],
  },
] as const

const CONTROLS = [
  [
    '01',
    'Full handoff logs',
    'See the conversation, rationale, owner, and outcome.',
    'switch',
  ],
  [
    '02',
    'Staged rollouts',
    'Sandbox, limited cohort, then wider deployment.',
    'staged',
  ],
  [
    '03',
    'Kill switch & rollback',
    'Stop an agent or revert a workflow path.',
    'switch',
  ],
  [
    '04',
    'Confidence escalation',
    'Bring in a person below the agreed threshold.',
    'human',
  ],
  [
    '05',
    'Conflict detection',
    'Flag contradictory agent actions for reconciliation.',
    'weekly',
  ],
] as const

const TIMELINE = [
  [
    'Weeks 01–02',
    'Discover',
    [
      'Workflow analysis',
      'Architecture plan',
      'Integration requirements',
      'Week-one baseline',
    ],
  ],
  [
    'Weeks 03–06',
    'Build',
    [
      '2–4 agents',
      'Shared memory layer',
      'CRM and tool integrations',
      'Weekly demos',
    ],
  ],
  [
    'Weeks 07–08',
    'Deploy',
    [
      'Staged production rollout',
      'Performance tuning',
      'Guardrail verification',
      'Team training',
    ],
  ],
  [
    'Weeks 09–12',
    'Optimize',
    [
      'Baseline comparison',
      'Workflow iterations',
      'Performance reporting',
      'Documentation and handoff',
    ],
  ],
] as const

const YOU_OWN = [
  ['Application source code', 'Custom agent UIs and routes'],
  ['Workflow logic', 'Your operating rules'],
  ['Integrations', 'CRM and connected tools'],
  ['Customer database', 'Records and logs in your environment'],
  ['Deployment infrastructure', 'Your account and access'],
  ['Documentation', 'Technical and operating handoff'],
] as const

const WE_OPERATE = [
  ['Hive Intelligence Platform', 'Proprietary layer'],
  ['Orchestration engine', 'Agent coordination'],
  ['Shared-memory layer', 'Context processing'],
  ['Trained models', 'Ongoing optimization'],
  ['LLM calls and embeddings', 'Usage infrastructure'],
  ['Security and support', 'Updates and priority support'],
] as const

const PRICE_ROWS = [
  [
    '$3,000/mo',
    'Platform, LLM usage, infrastructure, model optimization, security updates, and priority support.',
    '12-month minimum',
  ],
  ['$2', 'Per successful resolution.', 'Contract definition required'],
  [
    '15% off',
    'Annual prepay: $30,600/year, or $2,550/month effective.',
    'Annual option',
  ],
] as const

export default function HivePageContent() {
  const rootRef = useRef<HTMLDivElement>(null)
  const [enhanced, setEnhanced] = useState(false)
  const [activeUse, setActiveUse] = useState<string>('hr')
  const [relayRun, setRelayRun] = useState(0)

  useEffect(() => setEnhanced(true), [])

  // Reveal-on-scroll.
  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const targets = root.querySelectorAll<HTMLElement>('.hv-reveal')
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach(el => el.classList.add('hv-visible'))
      return
    }
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('hv-visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.08 }
    )
    targets.forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const useCase = USE_CASES.find(u => u.id === activeUse) ?? USE_CASES[3]

  return (
    <div ref={rootRef} className={`hive-page${enhanced ? ' is-enhanced' : ''}`}>
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className='hv-hero' id='top'>
        <div className='hv-wrap'>
          <div className='hv-hero-grid'>
            <div className='hv-hero-copy hv-reveal'>
              <div className='hv-pilot-line'>
                <p className='hv-label hv-label-pink'>
                  Hive multiplies · the colony works as one
                </p>
                <span className='hv-live-pill'>
                  <i className='hv-ping' aria-hidden='true' /> Pilot program
                  open
                </span>
              </div>
              <h1 className='hv-display'>
                One request.
                <br />
                The right agent.
                <br />
                <span className='hv-pink'>Full context.</span>
              </h1>
              <p className='hv-hero-lede'>
                <strong>
                  Hive turns isolated AI tools into a coordinated workforce.
                </strong>{' '}
                Specialized agents share customer context, hand work off, and
                bring in a person when confidence is low—built in 12 weeks on
                infrastructure you control.
              </p>
              <div className='hv-hero-actions'>
                <LinkButton
                  href='/contact-us'
                  variant='action'
                  size='lg'
                  data-cta-id='hive_hero_pilot'
                >
                  Request pilot access
                </LinkButton>
                {/* Replaces the mock's "Watch a handoff" jump link, on Lloyd's
                    instruction. /hive/why-hive is the comparison page and had
                    no inbound link from any rendered page; the handoff section
                    is still reachable by scrolling. */}
                <LinkButton
                  href='/hive/why-hive'
                  variant='neutral'
                  size='lg'
                  data-cta-id='hive_hero_why_hive'
                >
                  Why Hive
                </LinkButton>
              </div>
              <p className='hv-fit-line'>
                <strong>Best fit:</strong> B2B companies with 3+ AI touchpoints
                managing 50+ daily interactions.
              </p>
            </div>

            {/* Context relay — replaces the previous "live handoff theater"
                console, ported from `public/Hive by Pixelmojo — One Request.
                Full Context..html`. The animation is SVG SMIL + CSS keyframes,
                so it needs no JS and no React state. */}
            <figure
              className='hv-context-relay hv-reveal'
              id='handoff'
              aria-labelledby='relayCaption'
            >
              <div className='hv-relay-head'>
                <span>Context relay / H-01</span>
                <div className='hv-relay-head-actions'>
                  <span className='hv-relay-ready'>
                    <i aria-hidden='true' /> Ready
                  </span>
                  <button
                    className='hv-relay-replay'
                    type='button'
                    onClick={() => setRelayRun(run => run + 1)}
                    aria-label='Replay the Hive routing sequence'
                  >
                    <svg viewBox='0 0 16 16' aria-hidden='true'>
                      <path d='M13.2 6.1A5.5 5.5 0 1 0 13 10.4M13.2 2.8v3.3H9.9' />
                    </svg>
                    <span>Replay</span>
                  </button>
                </div>
              </div>

              <div className='hv-relay-art' key={relayRun}>
                <svg
                  viewBox='0 0 600 420'
                  role='img'
                  aria-labelledby='relayTitle relayDescription'
                >
                  <title id='relayTitle'>
                    A single Hive request routes with its full context bundle
                  </title>
                  <desc id='relayDescription'>
                    One illustrative request carries account, intent, urgency,
                    and history into the Hive shared core and onward to the
                    selected lead-intelligence agent. The context bundle stays
                    intact throughout the handoff.
                  </desc>
                  <defs>
                    <pattern
                      id='relayGrid'
                      width='32'
                      height='32'
                      patternUnits='userSpaceOnUse'
                    >
                      <path className='hv-relay-grid-path' d='M32 0H0V32' />
                    </pattern>
                  </defs>
                  <rect
                    className='hv-relay-grid'
                    width='600'
                    height='420'
                    fill='url(#relayGrid)'
                  />
                  <circle
                    className='hv-relay-orbit'
                    cx='306'
                    cy='210'
                    r='150'
                  />

                  <path className='hv-relay-wire' d='M130 212H252' />
                  <path
                    className='hv-relay-wire'
                    d='M360 210C418 210 430 82 480 82'
                  />
                  <path className='hv-relay-wire' d='M360 210H480' />
                  <path
                    className='hv-relay-wire'
                    d='M360 210C418 210 430 338 480 338'
                  />
                  <path
                    className='hv-relay-active hv-relay-in'
                    pathLength='1'
                    d='M130 212H252'
                  />
                  <path
                    className='hv-relay-active hv-relay-out hv-relay-out-lead'
                    pathLength='1'
                    d='M360 210C418 210 430 82 480 82'
                  />
                  <path
                    className='hv-relay-active hv-relay-out hv-relay-out-support'
                    pathLength='1'
                    d='M360 210H480'
                  />
                  <path
                    className='hv-relay-active hv-relay-out hv-relay-out-operations'
                    pathLength='1'
                    d='M360 210C418 210 430 338 480 338'
                  />

                  <rect
                    className='hv-relay-node'
                    x='38'
                    y='184'
                    width='92'
                    height='56'
                  />
                  <text
                    className='hv-relay-node-label'
                    x='84'
                    y='208'
                    textAnchor='middle'
                  >
                    REQUEST
                  </text>
                  <text
                    className='hv-relay-micro'
                    x='84'
                    y='226'
                    textAnchor='middle'
                  >
                    IN / 01
                  </text>

                  <rect
                    className='hv-relay-core'
                    x='252'
                    y='156'
                    width='108'
                    height='108'
                  />
                  <g transform='translate(274 178)' aria-hidden='true'>
                    <rect
                      className='hv-relay-mark-light'
                      x='4'
                      y='4'
                      width='26'
                      height='26'
                    />
                    <rect
                      className='hv-relay-core-pink'
                      x='34'
                      y='4'
                      width='11'
                      height='11'
                    />
                    <rect
                      className='hv-relay-core-pink'
                      x='49'
                      y='4'
                      width='11'
                      height='11'
                    />
                    <rect
                      className='hv-relay-core-pink'
                      x='34'
                      y='19'
                      width='11'
                      height='11'
                    />
                    <rect
                      className='hv-relay-core-pink'
                      x='49'
                      y='19'
                      width='11'
                      height='11'
                    />
                    <rect
                      className='hv-relay-mark-light'
                      x='11.5'
                      y='41.5'
                      width='11'
                      height='11'
                    />
                    <rect
                      className='hv-relay-mark-light'
                      x='34'
                      y='34'
                      width='26'
                      height='26'
                    />
                  </g>
                  <text
                    className='hv-relay-core-label'
                    x='306'
                    y='282'
                    textAnchor='middle'
                  >
                    HIVE CORE
                  </text>

                  <text
                    className='hv-relay-dest-label'
                    x='468'
                    y='79'
                    textAnchor='end'
                  >
                    LEAD INTEL
                  </text>
                  <rect
                    className='hv-relay-node hv-relay-target hv-relay-target-lead'
                    x='480'
                    y='54'
                    width='76'
                    height='56'
                  />
                  <text
                    className='hv-relay-node-label'
                    x='518'
                    y='88'
                    textAnchor='middle'
                  >
                    LI
                  </text>
                  <text
                    className='hv-relay-dest-label'
                    x='468'
                    y='207'
                    textAnchor='end'
                  >
                    SUPPORT
                  </text>
                  <rect
                    className='hv-relay-node hv-relay-target hv-relay-target-support'
                    x='480'
                    y='182'
                    width='76'
                    height='56'
                  />
                  <text
                    className='hv-relay-node-label'
                    x='518'
                    y='216'
                    textAnchor='middle'
                  >
                    SP
                  </text>
                  <text
                    className='hv-relay-dest-label'
                    x='468'
                    y='335'
                    textAnchor='end'
                  >
                    OPERATIONS
                  </text>
                  <rect
                    className='hv-relay-node hv-relay-target hv-relay-target-operations'
                    x='480'
                    y='310'
                    width='76'
                    height='56'
                  />
                  <text
                    className='hv-relay-node-label'
                    x='518'
                    y='344'
                    textAnchor='middle'
                  >
                    OP
                  </text>
                  <text
                    className='hv-relay-full-context hv-relay-full-context-lead hv-relay-micro'
                    x='518'
                    y='128'
                    textAnchor='middle'
                  >
                    FULL CONTEXT
                  </text>
                  <text
                    className='hv-relay-full-context hv-relay-full-context-support hv-relay-micro'
                    x='518'
                    y='256'
                    textAnchor='middle'
                  >
                    FULL CONTEXT
                  </text>
                  <text
                    className='hv-relay-full-context hv-relay-full-context-operations hv-relay-micro'
                    x='518'
                    y='384'
                    textAnchor='middle'
                  >
                    FULL CONTEXT
                  </text>

                  {RELAY_ROUTES.map(route => (
                    <Fragment key={route.id}>
                      <g className='hv-bundle-motion' aria-hidden='true'>
                        <ContextBundleSquares />
                        <animate
                          attributeName='opacity'
                          values={route.opacity}
                          keyTimes={route.opacityTimes}
                          dur='2.1s'
                          begin={route.begin}
                          fill='freeze'
                        />
                        <animateMotion
                          path={route.path}
                          keyPoints={route.keyPoints}
                          keyTimes='0;.12;.42;.52;.88;1'
                          calcMode='linear'
                          dur='2.1s'
                          begin={route.begin}
                          fill='freeze'
                        />
                      </g>
                      <g
                        className='hv-bundle-static'
                        transform={route.end}
                        aria-hidden='true'
                      >
                        <ContextBundleSquares />
                      </g>
                    </Fragment>
                  ))}
                </svg>
              </div>

              <div
                className='hv-relay-legend'
                aria-label='Context bundle contents'
              >
                {BUNDLE_KEYS.map(([token, label]) => (
                  <span
                    className='hv-bundle-key'
                    key={label}
                    style={{ '--h-key': token } as React.CSSProperties}
                  >
                    <i aria-hidden='true' />
                    {label}
                  </span>
                ))}
              </div>

              <figcaption className='hv-relay-caption' id='relayCaption'>
                <span>Illustrative routing</span>
                <span>No production activity</span>
              </figcaption>
            </figure>
          </div>

          <div
            className='hv-commercial-rail hv-reveal'
            aria-label='Hive commercial summary'
          >
            {COMMERCIAL.map(([label, value, note]) => (
              <div className='hv-commercial-cell' key={label}>
                <p className='hv-label hv-label-muted'>{label}</p>
                <strong>{value}</strong>
                <span>{note}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Explain it once ──────────────────────────────────────────── */}
      <section className='hv-once' id='how'>
        <div className='hv-once-grid'>
          <div className='hv-once-copy hv-reveal'>
            <p className='hv-label hv-label-pink'>
              The handoff, without the reset
            </p>
            <h2 className='hv-display'>
              Your customer should only explain it once.
            </h2>
            <p>
              Hive keeps one customer state alive as work moves between
              specialized agents. The next agent sees what happened, why it
              matters, and what it owns now.
            </p>
          </div>
          <div className='hv-trace-board hv-reveal'>
            <article className='hv-trace-block'>
              <div className='hv-trace-head'>
                <p className='hv-label'>Without Hive</p>
                <span className='hv-trace-tag hv-bad'>Context resets ×2</span>
              </div>
              <div className='hv-reset-row'>
                {RESET_CARDS.map(([who, quote], i) => (
                  <Fragment key={who}>
                    <div className='hv-reset-card'>
                      <strong>{who}</strong>
                      <p>{quote}</p>
                    </div>
                    {i < RESET_CARDS.length - 1 && (
                      <div className='hv-reset-icon' aria-hidden='true'>
                        ×
                      </div>
                    )}
                  </Fragment>
                ))}
              </div>
            </article>
            <article className='hv-trace-block'>
              <div className='hv-trace-head'>
                <p className='hv-label'>With Hive</p>
                <span className='hv-trace-tag hv-good'>One context thread</span>
              </div>
              <div className='hv-thread-card'>
                <div className='hv-thread-context'>
                  {THREAD_CONTEXT.map(([k, v]) => (
                    <div key={k}>
                      <span>{k}</span>
                      <strong>{v}</strong>
                    </div>
                  ))}
                </div>
                <div className='hv-handoff-row'>
                  {HANDOFF_STEPS.map(([n, who, what]) => (
                    <div className='hv-handoff-step' key={n}>
                      <i>{n}</i>
                      <strong>{who}</strong>
                      <small>{what}</small>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ── Shared intelligence ──────────────────────────────────────── */}
      <section className='hv-brain' aria-labelledby='brain-title'>
        <div className='hv-wrap'>
          <div className='hv-brain-head hv-reveal'>
            <div>
              <p className='hv-label'>Most AI tools are isolated</p>
              <h2 className='hv-display' id='brain-title'>
                One shared intelligence layer.
              </h2>
            </div>
            <p>
              Hive agents keep their specialties. The difference is that they
              read from the same customer state, transfer the full thread, and
              coordinate through a shared orchestration layer.
            </p>
          </div>
          <div className='hv-architecture hv-reveal'>
            <div className='hv-arch-side'>
              {ARCH_LEFT.map(([cls, icon, title, copy]) => (
                <article className={`hv-arch-card ${cls}`} key={title}>
                  <span className='hv-arch-icon'>{icon}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
            <div className='hv-arch-core'>
              <div className='hv-core-stack'>
                <HiveMark className='hv-hive-mark' />
                <h3>Hive core</h3>
                <p>Memory · orchestration · handoffs</p>
                <span className='hv-vector-strip'>
                  Vector included · 12-dimension intelligence
                </span>
              </div>
            </div>
            <div className='hv-arch-side'>
              {ARCH_RIGHT.map(([cls, icon, title, copy]) => (
                <article className={`hv-arch-card ${cls}`} key={title}>
                  <span className='hv-arch-icon'>{icon}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
          </div>
          <div className='hv-benefit-strip hv-reveal'>
            {BENEFITS.map(([title, copy]) => (
              <div className='hv-benefit' key={title}>
                <b>{title}</b>
                <p>{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Agent configurations ─────────────────────────────────────── */}
      <section className='hv-agents' id='agents'>
        <div className='hv-wrap'>
          <div className='hv-agents-head hv-reveal'>
            <div>
              <p className='hv-label hv-label-pink'>Choose the work</p>
              <h2 className='hv-display'>Hive assembles the team.</h2>
            </div>
            <p>
              The initial build includes 2–4 specialized agents. These examples
              show how the same shared-context system can be configured for
              different operating environments.
            </p>
          </div>
          <div
            className='hv-agent-tabs hv-reveal'
            role='tablist'
            aria-label='Example Hive configurations'
          >
            {USE_CASES.map(u => (
              <button
                key={u.id}
                className='hv-agent-tab'
                type='button'
                role='tab'
                aria-selected={activeUse === u.id}
                onClick={() => setActiveUse(u.id)}
              >
                {u.tab}
              </button>
            ))}
          </div>

          <article
            className='hv-use-panel hv-active'
            id={`use-${useCase.id}`}
            role='tabpanel'
          >
            <div className='hv-use-copy'>
              <span className='hv-use-number'>{useCase.number}</span>
              <h3>{useCase.title}</h3>
              <p>{useCase.copy}</p>
              <div className='hv-capability-list'>
                {useCase.capabilities.map(c => (
                  <div className='hv-capability' key={c}>
                    {c}
                  </div>
                ))}
              </div>
            </div>
            <div className='hv-workflow-panel'>
              <div className='hv-workflow-inner'>
                <span className='hv-illustrative'>
                  Illustrative configuration
                </span>
                <div className='hv-workflow-chain'>
                  {useCase.chain.map(([n, who, what, tag]) => (
                    <div className='hv-workflow-step' key={n}>
                      <i>{n}</i>
                      <span>
                        <strong>{who}</strong>
                        <small>{what}</small>
                      </span>
                      <em>{tag}</em>
                    </div>
                  ))}
                </div>
                <div className='hv-impact-box'>
                  <p className='hv-label'>{useCase.impactLabel}</p>
                  <div className='hv-impact-row'>
                    {useCase.impact.map(i => (
                      <span key={i}>{i}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* ── Human control ────────────────────────────────────────────── */}
      <section className='hv-control' id='safety'>
        <div className='hv-wrap hv-control-grid'>
          <div className='hv-control-copy hv-reveal'>
            <p className='hv-label'>What if an agent goes rogue?</p>
            <h2 className='hv-display'>Human control is part of the system.</h2>
            <p>
              Autonomy needs brakes, traces, and a clear owner. Hive is designed
              around staged deployment and visible handoffs—not an invisible
              black box.
            </p>
            <p className='hv-control-note'>
              <strong>Measurement starts in week one.</strong> Pixelmojo
              establishes a baseline and compares outcomes weekly. Your team
              decides whether the system is performing.
            </p>
          </div>
          <div
            className='hv-control-room hv-reveal'
            aria-label='Illustrative governance control room'
          >
            <div className='hv-control-room-head'>
              <p className='hv-label'>Governance controls</p>
              <span>● Systems nominal</span>
            </div>
            {CONTROLS.map(([n, title, copy, kind]) => (
              <div className='hv-control-row' key={n}>
                <i>{n}</i>
                <span>
                  <strong>{title}</strong>
                  <small>{copy}</small>
                </span>
                {kind === 'switch' ? (
                  <span className='hv-switch' aria-label='Enabled' />
                ) : (
                  <span className='hv-threshold'>{kind}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The build ────────────────────────────────────────────────── */}
      <section className='hv-build' aria-labelledby='build-title'>
        <div className='hv-wrap'>
          <div className='hv-build-head hv-reveal'>
            <div>
              <p className='hv-label hv-label-pink'>
                Fixed scope · founder-led delivery
              </p>
              <h2 className='hv-display' id='build-title'>
                12 weeks.
                <br />
                Four controlled releases.
              </h2>
            </div>
            <p>
              Start with the workflow and risk. Build 2–4 specialized agents.
              Release in stages. Finish with the source, documentation,
              infrastructure access, and a trained team.
            </p>
          </div>
          <div className='hv-timeline hv-reveal'>
            {TIMELINE.map(([week, title, items]) => (
              <article className='hv-timeline-card' key={title}>
                <span className='hv-week'>{week}</span>
                <h3>{title}</h3>
                <ul>
                  {items.map(i => (
                    <li key={i}>{i}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className='hv-handoff-band hv-reveal'>
            <strong>What leaves with you</strong>
            <span>
              Application source · custom agent interfaces · workflow logic ·
              integrations · deployment access · database · technical
              documentation · team training
            </span>
          </div>
        </div>
      </section>

      {/* ── Ownership boundary ───────────────────────────────────────── */}
      <section className='hv-ownership' aria-labelledby='own-title'>
        <div className='hv-wrap'>
          <div className='hv-ownership-head hv-reveal'>
            <div>
              <p className='hv-label hv-label-pink'>An honest boundary</p>
              <h2 className='hv-display' id='own-title'>
                Own the application. Keep the intelligence maintained.
              </h2>
            </div>
            <p>
              Hive is neither a pure software purchase nor a conventional
              seat-based SaaS. You own the custom application around your
              business. Pixelmojo continues to operate the proprietary
              intelligence layer.
            </p>
          </div>
          <div className='hv-boundary hv-reveal'>
            <div className='hv-boundary-col'>
              <div className='hv-boundary-title'>
                <h3>You own</h3>
                <span>Forever · client-controlled</span>
              </div>
              {YOU_OWN.map(([title, note]) => (
                <div className='hv-boundary-row' key={title}>
                  <strong>{title}</strong>
                  <span>{note}</span>
                </div>
              ))}
            </div>
            <div className='hv-boundary-col'>
              <div className='hv-boundary-title'>
                <h3>Pixelmojo operates</h3>
                <span>Included in platform fee</span>
              </div>
              {WE_OPERATE.map(([title, note]) => (
                <div className='hv-boundary-row' key={title}>
                  <strong>{title}</strong>
                  <span>{note}</span>
                </div>
              ))}
            </div>
          </div>
          <div className='hv-ownership-note hv-reveal'>
            <i>!</i>
            <p>
              <strong>
                The application keeps running on infrastructure you control.
              </strong>{' '}
              Ongoing Hive intelligence depends on the paid platform layer; the
              first 12 months are mandatory. Customer records and logs remain in
              your environment, while relevant context is processed by the
              intelligence service.
            </p>
          </div>
        </div>
      </section>

      {/* ── Pricing ──────────────────────────────────────────────────── */}
      <section className='hv-pricing' id='pricing'>
        <div className='hv-wrap'>
          <div className='hv-pricing-head hv-reveal'>
            <div>
              <p className='hv-label'>Transparent commercial model</p>
              <h2 className='hv-display'>
                One build.
                <br />
                One platform.
                <br />
                Usage tied to work.
              </h2>
            </div>
            <p>
              No hidden “contact us” price. The fixed scope starts with 2–4
              agents and includes Vector. Resolution billing needs an agreed
              event definition before launch.
            </p>
          </div>
          <div className='hv-price-ledger hv-reveal'>
            <div className='hv-price-primary'>
              <span className='hv-price-kicker'>One-time build · 12 weeks</span>
              <p className='hv-price-big'>
                <sup>$</sup>65K
              </p>
              <p>
                Discovery, architecture, 2–4 specialized agents, shared memory,
                integrations, staged deployment, optimization, documentation,
                source-code handoff, and team training.
              </p>
              <div className='hv-price-chips'>
                <span className='hv-price-chip'>Vector included</span>
                <span className='hv-price-chip'>No per-seat fees</span>
                <span className='hv-price-chip'>Founder-led</span>
                <span className='hv-price-chip'>Fixed scope</span>
              </div>
            </div>
            <div className='hv-price-secondary'>
              {PRICE_ROWS.map(([amount, copy, note]) => (
                <div className='hv-price-row' key={amount}>
                  <strong>{amount}</strong>
                  <span>{copy}</span>
                  <small>{note}</small>
                </div>
              ))}
            </div>
          </div>
          <div className='hv-cost-truth hv-reveal'>
            <div>
              <strong>The fixed first-year commitment, shown plainly.</strong>
              <p>
                Build + 12 monthly platform payments, before
                successful-resolution charges. Annual prepay lowers the fixed
                total to $95,600.
              </p>
              {/* Not in the mock. /hive/why-hive had no inbound link from any
                  rendered page, so it was reachable only via sitemap and
                  llms.txt. Added here because pricing is where a buyer starts
                  comparing. */}
              <LinkButton
                href='/hive/why-hive'
                variant='link'
                className='mt-3 text-sm'
                data-cta-id='hive_pricing_why_hive'
              >
                Compare against SaaS, agency, and open-source builds
              </LinkButton>
            </div>
            <div className='hv-cost-number'>
              $101,000<small>before usage charges</small>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <section className='hv-faq' aria-labelledby='faq-title'>
        <div className='hv-wrap hv-faq-grid'>
          <div className='hv-faq-copy hv-reveal'>
            <p className='hv-label hv-label-pink'>Questions before a pilot</p>
            <h2 className='hv-display' id='faq-title'>
              No fog.
            </h2>
            <p>
              The six things an operator should understand before committing to
              a Hive build.
            </p>
          </div>
          <div className='hv-faq-list hv-reveal'>
            {HIVE_FAQS.map(([q, a]) => (
              <details key={q}>
                <summary>{q}</summary>
                <div className='hv-answer'>{a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ──────────────────────────────────────────────── */}
      <section className='hv-final-cta' id='pilot'>
        <div className='hv-final-inner hv-reveal'>
          <HiveMark className='hv-hive-mark' />
          <p className='hv-label hv-label-pink'>Let’s see if this fits</p>
          <h2 className='hv-display'>Map the first handoff.</h2>
          <p>
            Bring the workflow where context gets lost today. Pixelmojo will
            tell you whether Hive is the right-sized answer.
          </p>
          <div className='hv-final-actions'>
            <LinkButton
              href='/contact-us'
              variant='action'
              size='lg'
              data-cta-id='hive_final_pilot'
            >
              Request pilot access
            </LinkButton>
            <LinkButton
              href='mailto:founders@pixelmojo.io?subject=Hive%20Pilot%20Inquiry'
              variant='neutral'
              size='lg'
            >
              Email the founders
            </LinkButton>
          </div>
          <div className='hv-call-notes'>
            <span>30 minutes</span>
            <span>No deck</span>
            <span>Founder-led</span>
            <span>Response within one business day</span>
          </div>
        </div>
      </section>
    </div>
  )
}
